import { FieldValues, Path, useFormContext } from "react-hook-form";
import { BorrowerNotificationType } from "../../OBRSettings";
import * as React from "react";
import { isFieldRequiredByNotification } from "./AdditionalEmailGroup";
import { useLanguage } from "../../Language";
import { FormTextField } from "../../FormTextField";
import { Stack } from "@mui/system";

export interface AddressBooleanObject {
  line1: boolean;
  line2?: boolean;
  city: boolean;
  state: boolean;
  zip: boolean;
}

export interface AddressStringObject {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressFieldGroupProps<T extends FieldValues> {
  line1: Path<T>;
  line2?: Path<T>;
  city: Path<T>;
  state: Path<T>;
  zip: Path<T>;
  required?: boolean | AddressBooleanObject;
  hidden?: boolean | AddressBooleanObject;
  labels?: AddressStringObject;
  disabled?: boolean;
  notificationTypes?: BorrowerNotificationType[][];
}

export const AddressFieldGroup = <T extends FieldValues>({
  line1,
  line2,
  city,
  state,
  zip,
  required,
  hidden,
  labels,
  disabled,
  notificationTypes,
}: AddressFieldGroupProps<T>) => {
  const { translate } = useLanguage();
  const { trigger, getFieldState } = useFormContext<T>();
  const { isTouched: isLine1Touched } = getFieldState(line1);
  const { isTouched: isCityTouched } = getFieldState(city);
  const { isTouched: isStateTouched } = getFieldState(state);
  const { isTouched: isZipTouched } = getFieldState(zip);

  const fieldLabels: AddressStringObject = {
    line1: translate("ci_address1"),
    line2: translate("ci_address2"),
    city: translate("ci_city"),
    state: translate("ci_state"),
    zip: translate("ci_zipCode"),
    ...labels,
  };

  const isFieldTrue = (
    key: keyof AddressBooleanObject,
    test: boolean | AddressBooleanObject
  ) => test === true || (typeof test === "object" && test[key] === true);

  const addressRequired = React.useMemo(() => {
    const isAddressRequiredByNotifications = isFieldRequiredByNotification(
      notificationTypes,
      BorrowerNotificationType.Paper
    );
    return {
      line1: isFieldTrue("line1", required)
        ? translate("isRequired", {
            LABEL: fieldLabels.line1,
          })
        : isAddressRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: fieldLabels.line1,
            NOTICE_TYPE: translate("ci_notificationType_Paper"),
          })
        : null,
      city: isFieldTrue("city", required)
        ? translate("isRequired", {
            LABEL: fieldLabels.city,
          })
        : isAddressRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: fieldLabels.city,
            NOTICE_TYPE: translate("ci_notificationType_Paper"),
          })
        : null,
      state: isFieldTrue("state", required)
        ? translate("isRequired", {
            LABEL: fieldLabels.state,
          })
        : isAddressRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: fieldLabels.state,
            NOTICE_TYPE: translate("ci_notificationType_Paper"),
          })
        : null,
      zip: isFieldTrue("zip", required)
        ? translate("isRequired", {
            LABEL: fieldLabels.zip,
          })
        : isAddressRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: fieldLabels.zip,
            NOTICE_TYPE: translate("ci_notificationType_Paper"),
          })
        : null,
    };
  }, [notificationTypes, required]);

  const isLine1Hidden = isFieldTrue("line1", hidden);
  const isLine2Hidden = isFieldTrue("line2", hidden);
  const isCityHidden = isFieldTrue("city", hidden);
  const isStateHidden = isFieldTrue("state", hidden);
  const isZipHidden = isFieldTrue("zip", hidden);
  const isAddressGroupHidden =
    isLine1Hidden && isCityHidden && isStateHidden && isZipHidden;

  React.useEffect(() => {
    if (isLine1Touched) {
      trigger(line1);
    }
  }, [!!addressRequired.line1]);

  React.useEffect(() => {
    if (isCityTouched) {
      trigger(city);
    }
  }, [!!addressRequired.city]);

  React.useEffect(() => {
    if (isStateTouched) {
      trigger(state);
    }
  }, [!!addressRequired.state]);

  React.useEffect(() => {
    if (isZipTouched) {
      trigger(zip);
    }
  }, [!!addressRequired.zip]);

  return (
    (addressRequired || !isAddressGroupHidden) && (
      <>
        {(!isLine1Hidden || !!addressRequired.line1) && (
          <FormTextField<T>
            sx={{ maxWidth: { sm: "320px", md: "648px" } }}
            name={line1}
            label={fieldLabels.line1}
            options={{ required: addressRequired.line1 }}
            disabled={disabled}
            maxLength={50}
          />
        )}
        {!isLine2Hidden && !!line2 && (
          <FormTextField<T>
            sx={{ maxWidth: { sm: "320px", md: "648px" } }}
            name={line2}
            maxLength={50}
            disabled={disabled}
            label={fieldLabels.line2}
          />
        )}
        {(!isCityHidden || !!addressRequired.city) && (
          <FormTextField<T>
            sx={{ maxWidth: { sm: "320px", md: "648px" } }}
            name={city}
            label={fieldLabels.city}
            options={{ required: addressRequired.city }}
            disabled={disabled}
            maxLength={30}
          />
        )}
        {(!isStateHidden ||
          !isZipHidden ||
          !!addressRequired.state ||
          !!addressRequired.zip) && (
          <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
            {(!isStateHidden || !!addressRequired.state) && (
              <FormTextField<T>
                disabled={disabled}
                sx={{ flexGrow: 1 }}
                name={state}
                label={fieldLabels.state}
                options={{ required: addressRequired.state }}
                maxLength={30}
              />
            )}
            {(!isZipHidden || !!addressRequired.zip) && (
              <FormTextField<T>
                disabled={disabled}
                sx={{ flexGrow: 1 }}
                name={zip}
                label={fieldLabels.zip}
                options={{ required: addressRequired.zip }}
                type="tel"
                maxLength={10}
              />
            )}
          </Stack>
        )}
      </>
    )
  );
};
